<template>
    <div>

        <PageContainer class="container mx-auto">

            <Breadcrumbs :breadcrumbs="breadcrumbs" />

            <div
                v-if="category.header_image.sizes.large"
                class="mb-8 hidden md:block"
            >
                <HeaderNavLink :url="category.header_image_url">
                    <CategoryImageHeading :image="category.header_image.sizes.large" />
                </HeaderNavLink>
            </div>

            <div
                v-if="category.header_html"
                class="my-5 wysiwyg-content"
                v-html="category.header_html"
            />

            <div v-if="category.check_delivery_postcode">
                <AddressCheckerResults
                    v-if="addressChecked"
                    class="mb-5"
                    :available="categoryAvailableForDelivery"
                    :postcode="postcode"
                    :price="deliveryPrice"
                />

                <ProductAddressChecker
                    v-else
                    :shipping-class="category.shipping_class_id"
                />
            </div>

            <CategoryChildren
                v-if="hasChildren"
                :children="category.children"
                :parent="category"
            />
            <CategoryProducts
                v-else
                :category="category"
            />

            <div
                v-if="category.footer_html"
                class="my-5 wysiwyg-content"
                v-html="category.footer_html"
            />

        </PageContainer>

        <client-only>
            <ProductAddedToBasketModal />
        </client-only>

    </div>
</template>

<script>
import CategoryChildren from '@/components/CategoryChildren';
import CategoryProducts from '@/components/CategoryProducts';
import Breadcrumbs from '~/components/Breadcrumbs';
import CategoryImageHeading from '~/components/CategoryImageHeading';
import ProductAddedToBasketModal from '~/components/ProductAddedToBasketModal';
import ProductAddressChecker from '~/components/ProductAddressChecker';
import AddressCheckerResults from '~/components/AddressCheckerResults';
import PageContainer from '@/components/PageContainer.vue';
import HeaderNavLink from '@/components/HeaderNavLink.vue';

export default {

    components: {
        HeaderNavLink,
        PageContainer,
        Breadcrumbs,
        CategoryImageHeading,
        ProductAddedToBasketModal,
        CategoryChildren,
        CategoryProducts,
        ProductAddressChecker,
        AddressCheckerResults,
    },

    asyncData({ $axios, params, error }) {
        return $axios.$get(`/categories/${params.slug}`)
            .then((category) => {
                const getBreadcrumbs = () => {
                    const breadcrumbs = [{
                        name: 'Home',
                        link: '/',
                    }];
                    if (category.parent) {
                        breadcrumbs.push({
                            name: category.parent.name,
                            link: `/categories/${category.parent.slug}`,
                        });
                    }
                    breadcrumbs.push({
                        name: category.name,
                        link: '',
                    });
                    return breadcrumbs;
                };

                return {
                    breadcrumbs: getBreadcrumbs(),
                    category,
                };
            })
            .catch((e) => {
                error({ statusCode: e.response.status });
            });
    },

    data() {
        return {
            category: {},
            breadcrumbs: [],
        };
    },

    computed: {
        hasChildren() {
            return this.category.children.length > 0;
        },
        addressChecked() {
            return this.$store.getters['checkout/addressChecked'](this.category.shipping_class_id);
        },
        deliveryPrice() {
            return this.$store.getters['checkout/getDeliveryPrice'](this.category.shipping_class_id);
        },
        categoryAvailableForDelivery() {
            if (!this.category.check_delivery_postcode) {
                return true;
            }

            return this.$store.getters['checkout/checkProductAvailable'](this.category.shipping_class_id);
        },
        postcode() {
            return this.$store.getters['checkout/shippingAddress'].value.postcode;
        },
    },

    methods: {
        resetDeliveryAddress() {
            this.$store.dispatch('checkout/resetProductsChecked');
        },
    },

    head() {
        return {
            title: this.category.meta_title,
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: this.category.meta_description,
                },
                {
                    hid: 'og:title',
                    name: 'og:title',
                    content: this.category.meta_title,
                },
                {
                    hid: 'og:image',
                    name: 'og:image',
                    content: this.category.header_image.sizes.large,
                },
            ],
        };
    },

};
</script>

<style lang="scss">
    .wysiwyg-content p {
        @apply mb-5;
    }
</style>
